import React, { useState, useEffect } from "react";
import { rulebookPageUrl, steamrollerPageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";

/**
 * Display the actual PDF pages (rendered as PNG images) for a rulebook or
 * Steamroller section.
 *
 * Props:
 *   pages       array of 1-indexed page numbers to show (from section.pages)
 *   chapter     label
 *   title       label
 *   source      "rulebook" | "steamroller" — defaults to "rulebook"
 *   activePage  (optional) controlled page number. When this changes to a page
 *               present in `pages`, the viewer jumps to it.
 */
export function PdfPagesViewer({ pages = [], chapter, title, source = "rulebook", activePage }) {
  const pageUrl = source === "steamroller" ? steamrollerPageUrl : rulebookPageUrl;
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  // When parent requests a specific page (e.g. clicking "see page 117"),
  // jump there if it's in our pages list.
  useEffect(() => {
    if (activePage == null) return;
    const i = pages.indexOf(activePage);
    if (i >= 0) setIndex(i);
  }, [activePage, pages]);

  if (!pages.length) return null;

  const page = pages[index];
  const prev = () => setIndex((i) => (i - 1 + pages.length) % pages.length);
  const next = () => setIndex((i) => (i + 1) % pages.length);

  return (
    <div data-testid="pdf-pages-viewer">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={prev}
            disabled={pages.length < 2}
            data-testid="pdf-page-prev"
            aria-label="Previous PDF page"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span
            className="text-xs text-muted-foreground tabular-nums px-1.5"
            data-testid="pdf-page-indicator"
          >
            Page {page} ({index + 1}/{pages.length})
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={next}
            disabled={pages.length < 2}
            data-testid="pdf-page-next"
            aria-label="Next PDF page"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLightbox(true)}
          className="text-xs"
          data-testid="pdf-page-expand"
        >
          <Maximize2 className="w-3.5 h-3.5 mr-1" /> Open full size
        </Button>
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {pages.map((p, i) => (
          <button
            key={p}
            type="button"
            onClick={() => setIndex(i)}
            className={`text-[11px] px-2 py-0.5 rounded border transition ${
              i === index
                ? "border-primary/60 bg-primary/15 text-primary"
                : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
            }`}
            data-testid={`pdf-page-chip-${p}`}
          >
            p{p}
          </button>
        ))}
      </div>
      <div className="rounded-md border border-border bg-black/40 overflow-hidden">
        <img
          src={pageUrl(page)}
          alt={`${chapter} ${title} — page ${page}`}
          className="w-full h-auto block"
          data-testid="pdf-page-image"
          loading="lazy"
        />
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
          data-testid="pdf-page-lightbox"
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 text-white hover:bg-white/10"
            onClick={() => setLightbox(false)}
            data-testid="pdf-page-lightbox-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </Button>
          <img
            src={pageUrl(page)}
            alt={`${title} page ${page}`}
            className="max-w-full max-h-full object-contain shadow-2xl rounded"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
